import { AuthContext } from '@core/shared';
import { MarketingCampaignStatus, OptionsApi } from '@core/swagger-client';
import { Button, ContainerDefault, ErrorMessage, ErrorMessagePage, Heading, LoaderPage } from '@core/ui';
import { useNavigate, useParams } from '@online-webinar/router';
import { BackLink } from '@online-webinar/ui-frontend';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';

const fullName = (firstName?: string | null, lastName?: string | null) => [firstName, lastName].filter(Boolean).join(' ');

export const SelectCampaignListPage = () => {
    const canManageEmailLists = useContext(AuthContext).canManageEmailLists;
    const { t } = useTranslation();
    const nav = useNavigate();

    const { webinarId, id } = useParams({ strict: false });
    const wid = webinarId ?? '';
    const campaignId = id ?? '';

    const [selectedListId, setSelectedListId] = useState<string>();

    const { data: campaign, isLoading: campaignLoading, error: campaignError } = useQuery(
        OptionsApi.marketingCampaigns.getDetail(wid, campaignId, { enabled: !!wid && !!campaignId })
    );
    const { data: lists, isLoading: listsLoading } = useQuery(
        OptionsApi.marketingCampaigns.getLists(wid, { enabled: !!wid })
    );
    const { data: recipients, isLoading: recipientsLoading } = useQuery(
        OptionsApi.emailLists.getRecipients(selectedListId ?? '', { enabled: !!selectedListId })
    );

    const updateMutation = useMutation(OptionsApi.marketingCampaigns.update());
    const sendMoreMutation = useMutation(OptionsApi.marketingCampaigns.sendMore());

    if (!canManageEmailLists) {
        return <ErrorMessagePage statusCode={404} />;
    }
    if (campaignError) {
        return <ErrorMessagePage apiError={campaignError} />;
    }
    if (campaignLoading || listsLoading || !campaign) {
        return <LoaderPage />;
    }

    const targeted = new Set(campaign.targetListIds);
    const available = (lists?.global ?? []).filter((l) => !targeted.has(l.id));
    const selectedList = available.find((l) => l.id === selectedListId);

    const goBack = () => nav({ to: '/$webinarId/marketing/$id', params: { webinarId: wid, id: campaignId } });

    const handleUse = () => {
        if (!selectedListId) return;
        const isDraft = campaign.status === MarketingCampaignStatus.Draft;
        if (isDraft) {
            updateMutation.mutate(
                {
                    webinarId: wid,
                    id: campaignId,
                    name: campaign.name,
                    subject: campaign.subject,
                    body: campaign.body,
                    excludeInterested: campaign.excludeInterested,
                    targetListIds: [...campaign.targetListIds, selectedListId]
                },
                { onSuccess: goBack }
            );
        } else {
            sendMoreMutation.mutate(
                { webinarId: wid, id: campaignId, listIds: [selectedListId] },
                { onSuccess: goBack }
            );
        }
    };

    const mutationError = updateMutation.error ?? sendMoreMutation.error;
    const isMutating = updateMutation.isPending || sendMoreMutation.isPending;

    if (selectedList) {
        return (
            <ContainerDefault>
                <div className="mb-4 md:mb-10">
                    <Button variant="secondary-outline" onClick={() => setSelectedListId(undefined)}>
                        ← {t('Campaign.SelectList.Back')}
                    </Button>
                </div>
                <Heading variant="h1" classNames="mb-4 lg:mb-6 2xl:mb-8">
                    {selectedList.description}
                </Heading>

                {mutationError && <ErrorMessage apiError={mutationError} classNames="mb-4 max-w-4xl" />}

                {recipientsLoading ? (
                    <LoaderPage />
                ) : (
                    <div className="table-responsive bg-white rounded-lg shadow-md mb-6">
                        <table className="table table-striped mt-2">
                            <thead>
                                <tr>
                                    <th>{t('EmailLists.Detail.Members.Header.Email')}</th>
                                    <th>{t('EmailLists.Detail.Members.Header.Name')}</th>
                                    <th>{t('EmailLists.Detail.Members.Header.Company')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(recipients ?? []).map((r) => (
                                    <tr key={r.id}>
                                        <td>{r.emailAddress}</td>
                                        <td>{fullName(r.firstName, r.lastName)}</td>
                                        <td>{r.companyName}</td>
                                    </tr>
                                ))}
                                {(!recipients || recipients.length === 0) && (
                                    <tr>
                                        <td colSpan={3}>{t('EmailLists.Detail.Members.Empty')}</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                <div className="flex gap-4">
                    <Button variant="primary" isLoading={isMutating} disabled={isMutating} onClick={handleUse}>
                        {t('Campaign.SelectList.UseList')}
                    </Button>
                    <Button variant="secondary-outline" disabled={isMutating} onClick={goBack}>
                        {t('Generic.Button.Cancel')}
                    </Button>
                </div>
            </ContainerDefault>
        );
    }

    return (
        <ContainerDefault>
            <BackLink to="/$webinarId/marketing/$id" params={{ webinarId: wid, id: campaignId }}>
                {t('Button.Back')}
            </BackLink>
            <Heading variant="h1" classNames="mb-4 lg:mb-6 2xl:mb-8">
                {t('Campaign.SelectList.Heading')}
            </Heading>

            {available.length === 0 ? (
                <p className="text-gray-600">{t('Campaign.SelectList.Empty')}</p>
            ) : (
                <div className="flex flex-col gap-2 max-w-lg">
                    {available.map((list) => (
                        <button
                            key={list.id}
                            type="button"
                            className="flex items-center justify-between p-3 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-left"
                            onClick={() => setSelectedListId(list.id)}
                        >
                            <span>{list.description}</span>
                            <span className="text-sm text-gray-500 ml-4">{list.recipientCount}</span>
                        </button>
                    ))}
                </div>
            )}
        </ContainerDefault>
    );
};
